/**
 * WorkspaceSwitcher
 *
 * The workspace section of the avatar menu (Figma 9073:1436): every
 * workspace the user belongs to as a `DropdownItem` row — logo or initials,
 * name, check on the active one — followed by "+ Add New Workspace".  The
 * list is shown even with a single workspace so the current context is
 * always visible.  Picking another row calls POST /workspaces/{id}/switch
 * and opens the unfiltered Projects page so the previous workspace's
 * project and filters are not carried over; the add
 * button hands off to the owner, which opens `CreateWorkspaceModal`, and is
 * hidden for the one account the backend would refuse (`canCreateWorkspace`
 * from the bootstrap: a member whose Creator approval a God revoked).
 *
 * Invitations the user closed without deciding (Figma 10219:22909) follow
 * after a Divider as "Pending" rows; clicking one reopens the invitation
 * modal for it (`onOpenInvitation`).
 */
import React, { useState } from 'react';
import { Avatar, Button, Divider, DropdownItem, TagGeneral, TruncatedText } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import { goHomeAfterWorkspaceChange } from '../utils/goHomeAfterWorkspaceChange';
import { usePendingInvitations } from './PendingInvitationsProvider';
import './WorkspaceSwitcher.css';

/** DOM id of a workspace row; the "Welcome aboard" callout measures these to frame the new rows. */
export const workspaceRowId = (workspaceId) => `workspace-switcher-${workspaceId}`;

/** Workspace logo (data URL set in onboarding / Settings) or an initials fallback. */
export function WorkspaceAvatar({ workspace, size = 'small' }) {
    const name = workspace?.name || 'Workspace';
    if (workspace?.logo) {
        return <Avatar size={size} type="image" src={workspace.logo} alt="" className="workspace-switcher__avatar" />;
    }
    return <Avatar size={size} type="initials" primaryText={name} className="workspace-switcher__avatar" />;
}

export default function WorkspaceSwitcher({
    onAddWorkspace,
    onOpenInvitation,
    reload = goHomeAfterWorkspaceChange,
}) {
    const { workspace, workspaces, switchWorkspace, canCreateWorkspace } = useAuth();
    const { dismissed: pendingInvitations, openFor } = usePendingInvitations();
    const [switching, setSwitching] = useState(false);
    const list = Array.isArray(workspaces) ? workspaces : [];

    const handleOpenInvitation = (invitationId) => {
        openFor(invitationId);
        onOpenInvitation?.(invitationId);
    };

    const handleSwitch = async (wsId) => {
        if (wsId === workspace?.id || switching) return;
        setSwitching(true);
        try {
            await switchWorkspace(wsId);
            reload();
        } catch (err) {
            console.error('Workspace switch failed:', err);
            setSwitching(false);
        }
    };

    return (
        <div className="workspace-switcher" aria-busy={switching || undefined}>
            {list.length > 0 ? (
                <div className="workspace-switcher__list" role="group" aria-label="Workspaces">
                    {list.map((ws) => {
                        const isActive = ws.id === workspace?.id;
                        let state = 'default';
                        if (isActive) state = 'selected';
                        else if (switching) state = 'disabled';
                        return (
                            <DropdownItem
                                key={ws.id}
                                id={workspaceRowId(ws.id)}
                                className="workspace-switcher__item"
                                /* Long names truncate with "…" and reveal the full name
                                   on hover; the check icon stays at the row end. */
                                label={
                                    <span className="workspace-switcher__label">
                                        <TruncatedText
                                            text={ws.name}
                                            maxWidth="100%"
                                            className="workspace-switcher__name"
                                        />
                                        {ws.is_owner ? (
                                            <span className="workspace-switcher__owner">Owner</span>
                                        ) : null}
                                    </span>
                                }
                                prefix={<WorkspaceAvatar workspace={ws} />}
                                state={state}
                                onClick={() => handleSwitch(ws.id)}
                            />
                        );
                    })}
                </div>
            ) : null}
            {canCreateWorkspace ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="tiny"
                    fill
                    showStartIcon
                    className="workspace-switcher__add"
                    disabled={switching}
                    onClick={() => onAddWorkspace?.()}
                >
                    Add New Workspace
                </Button>
            ) : null}
            {pendingInvitations.length > 0 ? (
                <>
                    <Divider className="workspace-switcher__pending-divider" />
                    <div
                        className="workspace-switcher__list"
                        role="group"
                        aria-label="Pending invitations"
                        data-testid="workspace-switcher-pending"
                    >
                        {pendingInvitations.map((invitation) => (
                            <DropdownItem
                                key={invitation.id}
                                id={`workspace-invitation-${invitation.id}`}
                                className="workspace-switcher__item workspace-switcher__pending"
                                label={
                                    <span className="workspace-switcher__label">
                                        <TruncatedText
                                            text={invitation.workspace_name}
                                            maxWidth="100%"
                                            className="workspace-switcher__name"
                                        />
                                    </span>
                                }
                                prefix={
                                    <WorkspaceAvatar
                                        workspace={{ name: invitation.workspace_name, logo: invitation.workspace_logo }}
                                    />
                                }
                                suffix={
                                    <TagGeneral
                                        label="Pending"
                                        endIconSlug="clock-hour-icon"
                                        className="workspace-switcher__pending-tag"
                                    />
                                }
                                state={switching ? 'disabled' : 'default'}
                                onClick={() => handleOpenInvitation(invitation.id)}
                            />
                        ))}
                    </div>
                </>
            ) : null}
        </div>
    );
}
